import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api';

/**
 * Admin authoring for the Trust Center content the public page renders but settings couldn't
 * populate: subprocessors, gated documents, and the document access-request inbox. Wired to the
 * existing /trust-center backend endpoints. English-inline (admin-internal); gated by canManage.
 */

const EMPTY_SUB = { name: '', category: '', region: '', purpose: '', dataCategories: '', websiteUrl: '', sortOrder: 0, showOnTrust: true };

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}
function fmt(ts) { try { return ts ? new Date(ts).toLocaleString() : '—'; } catch { return ts; } }

// ── Subprocessors ───────────────────────────────────────────────────────────
function Subprocessors({ canManage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id being edited, or 'new', or null
  const [form, setForm] = useState(EMPTY_SUB);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await axiosInstance.get('/trust-center/subprocessors')).data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY_SUB); setEditing('new'); };
  const startEdit = (r) => {
    setForm({ name: r.name || '', category: r.category || '', region: r.region || '', purpose: r.purpose || '',
      dataCategories: r.dataCategories || r.data_categories || '', websiteUrl: r.websiteUrl || r.website_url || '',
      sortOrder: r.sortOrder ?? 0, showOnTrust: r.showOnTrust !== false });
    setEditing(r.id);
  };
  const save = async () => {
    if (!form.name.trim()) { alert('Name is required.'); return; }
    setBusy(true);
    try {
      const body = { ...form, sortOrder: Number(form.sortOrder) || 0 };
      if (editing === 'new') await axiosInstance.post('/trust-center/subprocessors', body);
      else await axiosInstance.put(`/trust-center/subprocessors/${editing}`, body);
      setEditing(null); await load();
    } catch { alert('Save failed.'); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this subprocessor?')) return;
    try { await axiosInstance.delete(`/trust-center/subprocessors/${id}`); await load(); }
    catch { alert('Delete failed.'); }
  };

  return (
    <div className="tc-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="tc-section-label" style={{ margin: 0 }}>Subprocessors</div>
        {canManage && editing === null && (
          <button className="tc-save-btn" onClick={startNew}><i className="fas fa-plus" /> Add subprocessor</button>
        )}
      </div>
      <p className="tc-toggle-desc" style={{ marginTop: 4 }}>Third parties you share data with — shown on your public trust page.</p>

      {editing !== null && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, margin: '12px 0', background: '#f8fafc' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['name', 'Name *'], ['category', 'Category'], ['region', 'Region(s)'], ['websiteUrl', 'Website URL'],
              ['dataCategories', 'Data categories shared']].map(([k, label]) => (
              <div key={k} className="tc-field" style={{ margin: 0 }}>
                <label className="tc-label">{label}</label>
                <input className="tc-input" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <div className="tc-field" style={{ margin: 0 }}>
              <label className="tc-label">Sort order</label>
              <input className="tc-input" type="number" value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))} />
            </div>
          </div>
          <div className="tc-field" style={{ marginTop: 12 }}>
            <label className="tc-label">Purpose</label>
            <textarea className="tc-input tc-textarea" rows={2} value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 14 }}>
            <input type="checkbox" checked={form.showOnTrust} onChange={e => setForm(f => ({ ...f, showOnTrust: e.target.checked }))} />
            Show on public trust page
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="tc-save-btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="tc-view-btn" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="tc-spinner-wrap"><div className="tc-spinner" /></div>
        : rows.length === 0 ? <p className="tc-toggle-desc">No subprocessors yet.</p>
        : (
          <table className="tc-table" style={{ width: '100%', marginTop: 8, fontSize: 14, borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #eef2f7' }}>
                  <td style={{ padding: '8px 6px', fontWeight: 600 }}>{r.name}
                    {r.showOnTrust === false && <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>(hidden)</span>}</td>
                  <td style={{ padding: '8px 6px', color: '#64748b' }}>{r.category || '—'}</td>
                  <td style={{ padding: '8px 6px', color: '#64748b' }}>{r.region || '—'}</td>
                  {canManage && <td style={{ padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="tc-view-btn" onClick={() => startEdit(r)}><i className="fas fa-pen" /></button>
                    <button className="tc-view-btn" style={{ marginLeft: 6, color: '#dc2626' }} onClick={() => remove(r.id)}><i className="fas fa-trash" /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────────
function Documents({ canManage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [up, setUp] = useState({ file: null, title: '', category: '', gated: true, showOnTrust: true });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await axiosInstance.get('/trust-center/documents')).data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!up.file || !up.title.trim()) { alert('Pick a file and enter a title.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', up.file); fd.append('title', up.title); fd.append('category', up.category || '');
      fd.append('gated', up.gated); fd.append('showOnTrust', up.showOnTrust); fd.append('sortOrder', 0);
      await axiosInstance.post('/trust-center/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUp({ file: null, title: '', category: '', gated: true, showOnTrust: true }); await load();
    } catch { alert('Upload failed.'); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await axiosInstance.delete(`/trust-center/documents/${id}`); await load(); }
    catch { alert('Delete failed.'); }
  };

  return (
    <div className="tc-card">
      <div className="tc-section-label">Documents</div>
      <p className="tc-toggle-desc" style={{ marginTop: 4 }}>SOC 2 reports, policies, pen-test letters. Gated documents require an access request.</p>

      {canManage && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, margin: '12px 0', background: '#f8fafc' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="tc-field" style={{ margin: 0 }}>
              <label className="tc-label">Title *</label>
              <input className="tc-input" value={up.title} onChange={e => setUp(u => ({ ...u, title: e.target.value }))} placeholder="SOC 2 Type II Report" />
            </div>
            <div className="tc-field" style={{ margin: 0 }}>
              <label className="tc-label">Category</label>
              <input className="tc-input" value={up.category} onChange={e => setUp(u => ({ ...u, category: e.target.value }))} placeholder="Compliance" />
            </div>
          </div>
          <div className="tc-field" style={{ marginTop: 12 }}>
            <label className="tc-label">File *</label>
            <input type="file" onChange={e => setUp(u => ({ ...u, file: e.target.files[0] || null }))} />
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 14 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={up.gated} onChange={e => setUp(u => ({ ...u, gated: e.target.checked }))} /> Gated (request required)
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={up.showOnTrust} onChange={e => setUp(u => ({ ...u, showOnTrust: e.target.checked }))} /> Show on trust page
            </label>
          </div>
          <button className="tc-save-btn" style={{ marginTop: 14 }} onClick={upload} disabled={busy}>
            {busy ? 'Uploading…' : <><i className="fas fa-upload" /> Upload</>}
          </button>
        </div>
      )}

      {loading ? <div className="tc-spinner-wrap"><div className="tc-spinner" /></div>
        : rows.length === 0 ? <p className="tc-toggle-desc">No documents yet.</p>
        : (
          <table style={{ width: '100%', marginTop: 8, fontSize: 14, borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #eef2f7' }}>
                  <td style={{ padding: '8px 6px', fontWeight: 600 }}>{r.title}</td>
                  <td style={{ padding: '8px 6px', color: '#64748b' }}>{r.category || '—'}</td>
                  <td style={{ padding: '8px 6px', color: '#64748b' }}>{fmtBytes(r.fileSize)}</td>
                  <td style={{ padding: '8px 6px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: r.gated ? '#d97706' : '#16a34a' }}>{r.gated ? 'GATED' : 'PUBLIC'}</span>
                    {r.showOnTrust === false && <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>hidden</span>}
                  </td>
                  {canManage && <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                    <button className="tc-view-btn" style={{ color: '#dc2626' }} onClick={() => remove(r.id)}><i className="fas fa-trash" /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

// ── Access requests inbox ─────────────────────────────────────────────────────
function AccessRequests({ canManage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await axiosInstance.get('/trust-center/document-requests', { params: { status: 'PENDING' } })).data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try { await axiosInstance.post(`/trust-center/document-requests/${id}/${action}`); await load(); }
    catch { alert(`${action} failed.`); }
  };

  return (
    <div className="tc-card">
      <div className="tc-section-label">Document access requests
        {rows.length > 0 && <span style={{ marginLeft: 8, background: '#dc2626', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 12 }}>{rows.length}</span>}
      </div>
      <p className="tc-toggle-desc" style={{ marginTop: 4 }}>Pending requests to access your gated documents. Approving emails the requester a time-limited link.</p>

      {loading ? <div className="tc-spinner-wrap"><div className="tc-spinner" /></div>
        : rows.length === 0 ? <p className="tc-toggle-desc">No pending requests.</p>
        : rows.map(r => (
          <div key={r.id} style={{ borderTop: '1px solid #eef2f7', padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 600 }}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || r.email} <span style={{ color: '#94a3b8', fontWeight: 400 }}>· {r.email}</span></div>
              <div style={{ color: '#64748b', fontSize: 13 }}>
                wants <b>{r.documentTitle}</b>{r.company ? ` · ${r.company}` : ''}{r.reason ? ` · ${r.reason}` : ''} · {fmt(r.createdAt)}
              </div>
            </div>
            {canManage && (
              <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                <button className="tc-save-btn" onClick={() => decide(r.id, 'approve')}><i className="fas fa-check" /> Approve</button>
                <button className="tc-view-btn" style={{ color: '#dc2626' }} onClick={() => decide(r.id, 'deny')}>Deny</button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// ── Security-report requests inbox ────────────────────────────────────────────
function SecurityReportRequests({ canManage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await axiosInstance.get('/trust-center/security-report-requests', { params: { status: 'PENDING' } })).data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try { await axiosInstance.post(`/trust-center/security-report-requests/${id}/${action}`); await load(); }
    catch { alert(`${action} failed.`); }
  };

  return (
    <div className="tc-card">
      <div className="tc-section-label">Security report requests
        {rows.length > 0 && <span style={{ marginLeft: 8, background: '#dc2626', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 12 }}>{rows.length}</span>}
      </div>
      <p className="tc-toggle-desc" style={{ marginTop: 4 }}>Pending requests for your full security-testing report (ASM / iASM / red-team). This report discloses your security posture, so it is only shared after you approve — approving emails the requester a time-limited link.</p>

      {loading ? <div className="tc-spinner-wrap"><div className="tc-spinner" /></div>
        : rows.length === 0 ? <p className="tc-toggle-desc">No pending requests.</p>
        : rows.map(r => (
          <div key={r.id} style={{ borderTop: '1px solid #eef2f7', padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 600 }}>{r.email}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>
                wants the <b>security-testing report</b>{r.company ? ` · ${r.company}` : ''} · {fmt(r.createdAt)}
              </div>
            </div>
            {canManage && (
              <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                <button className="tc-save-btn" onClick={() => decide(r.id, 'approve')}><i className="fas fa-check" /> Approve</button>
                <button className="tc-view-btn" style={{ color: '#dc2626' }} onClick={() => decide(r.id, 'deny')}>Deny</button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

export default function TrustCenterAuthoring({ canManage }) {
  return (
    <>
      <AccessRequests canManage={canManage} />
      <SecurityReportRequests canManage={canManage} />
      <Subprocessors canManage={canManage} />
      <Documents canManage={canManage} />
    </>
  );
}
